import { useGetDocumentWithAllVersions, useGetMcpConnections } from "@hooks";
import { WarningRounded } from "@mui/icons-material";
import { Chip, Typography } from "@mui/material";
import { type ReactNode, useMemo } from "react";
import { JSONTree } from "react-json-tree";
import { MarkdownText } from "../../../ui/MarkdownText";
import { isHumanInTheLoopRejection } from "../../human-in-the-loop";
import type { ChatToolMessage } from "../../types";
import { useChatDeviceContext } from "../../useChatDeviceContext";
import {
  DEFAULT_TOOL_CONFIG,
  getToolIntegrationIcon,
  TOOL_CONFIGS,
  ToolCallName,
} from "../utils/tool-calls";
import { isComplexValue, JSON_TREE_THEME, keyToLabel } from "../utils/utils";
import { PendingDocumentToolParameters } from "./PendingDocumentToolParameters";
import { ReadDocumentToolResult } from "./ReadDocumentToolResult";
import { ToolCallFrame } from "./ToolCallFrame";

export interface GenericToolCallProps {
  toolName: string;
  args: Record<string, any>;
  toolResult?: ChatToolMessage;
  isLoading?: boolean;
  approvalControls?: ReactNode;
}

function normalizeToolResultContent(content: unknown): {
  parsedContent: unknown;
  isJsonContent: boolean;
} {
  if (typeof content === "string") {
    try {
      const parsedContent = JSON.parse(content);
      return {
        parsedContent,
        isJsonContent: isComplexValue(parsedContent),
      };
    } catch {
      return {
        parsedContent: content,
        isJsonContent: false,
      };
    }
  }

  return {
    parsedContent: content,
    isJsonContent: isComplexValue(content),
  };
}

export const GenericToolCall = ({
  toolName,
  args,
  toolResult,
  isLoading = false,
  approvalControls,
}: GenericToolCallProps) => {
  const { orgId } = useChatDeviceContext();
  const rawDocumentId =
    typeof args.document_id === "string"
      ? args.document_id
      : ["read_document", "read_document_metadata"].includes(toolName) &&
          typeof args.id === "string"
        ? args.id
        : "";
  const documentId = rawDocumentId.split("/").filter(Boolean).at(-1) ?? "";
  const { data: document } = useGetDocumentWithAllVersions(orgId, documentId, {
    throwOnError: false,
  });
  const documents = useMemo(() => (document ? [document] : []), [document]);
  const { data: mcpConnectionsData } = useGetMcpConnections(orgId, !!orgId);

  const toolConfig = useMemo(() => {
    const normalizedToolName = toolName.toLowerCase();
    return (
      TOOL_CONFIGS[toolName] ||
      TOOL_CONFIGS[normalizedToolName] ||
      DEFAULT_TOOL_CONFIG
    );
  }, [toolName]);

  const customMcpConnections = mcpConnectionsData?.connections;
  const integrationIcon = useMemo(
    () => getToolIntegrationIcon(toolName, customMcpConnections),
    [customMcpConnections, toolName]
  );

  const toolTitle = useMemo(() => {
    const config =
      TOOL_CONFIGS[toolName] || TOOL_CONFIGS[toolName.toLowerCase()];
    if (config?.title) {
      return config.title(args, toolName, toolResult, documents);
    }
    return keyToLabel(toolName);
  }, [args, documents, toolName, toolResult]);

  const isRejected = isHumanInTheLoopRejection(toolResult);
  const pendingDocument =
    typeof args.document_id === "string"
      ? documents?.find(
          (document) =>
            document.id === args.document_id ||
            document.tiptapPath === args.document_id
        )
      : undefined;
  const pendingTarget =
    pendingDocument?.title ?? args.document_id ?? args.default_title;
  const pendingTitle =
    (approvalControls || isRejected) && typeof pendingTarget === "string"
      ? `${keyToLabel(toolName)} — ${pendingTarget}`
      : toolTitle;

  const hasToolResult = !!toolResult?.tool_call_id;
  const hasError = toolResult?.status === "error" && !isRejected;
  const inputPreview = useMemo(() => {
    const preview =
      args.description ?? args.prompt ?? args.query ?? args.input ?? null;
    return typeof preview === "string" && preview.trim().length > 0
      ? preview.trim()
      : null;
  }, [args]);
  const shouldShowInputPreview =
    isLoading && !!inputPreview && toolConfig.showResult !== false;
  const hasExpandableContent =
    !isRejected &&
    (!!toolResult || shouldShowInputPreview) &&
    toolConfig.showResult !== false;
  const isPendingReview = !!approvalControls && !toolResult;

  const parsedToolResult = useMemo(() => {
    if (!toolResult) return undefined;

    if (toolName === ToolCallName.READ_DOCUMENT) {
      return {
        isError: toolResult.status === "error",
        errorMessage: undefined,
        parsedContent: undefined,
        isJsonContent: false,
      };
    }

    if (toolResult.status === "error") {
      const errorMessage =
        typeof toolResult.content === "string"
          ? toolResult.content
          : toolConfig.errorMessage || "Failed to parse or execute tool";

      return {
        isError: true,
        errorMessage,
        parsedContent: undefined,
        isJsonContent: false,
      };
    }

    const { parsedContent, isJsonContent } = normalizeToolResultContent(
      toolResult.content
    );

    return {
      isError: false,
      parsedContent,
      isJsonContent,
      errorMessage: undefined,
    };
  }, [toolConfig.errorMessage, toolName, toolResult]);

  const toolResultContent = useMemo(() => {
    if (isRejected) {
      return null;
    }

    if (toolName === ToolCallName.READ_DOCUMENT) {
      return null;
    }

    if (toolConfig.showResult === false) {
      return null;
    }

    if (!toolResult && shouldShowInputPreview) {
      return <MarkdownText>{inputPreview}</MarkdownText>;
    }

    if (!toolResult || !parsedToolResult) return null;

    if (parsedToolResult.isError) {
      return (
        <Typography
          className="text-orange-500 flex items-center gap-1"
          component="span"
        >
          <WarningRounded className="h-4 w-4" />
          {parsedToolResult.errorMessage}
        </Typography>
      );
    }

    const { parsedContent, isJsonContent } = parsedToolResult;
    const isListDataKeys = toolName === ToolCallName.LIST_DATA_KEYS;
    const isReadDocumentMetadataTool =
      toolName === ToolCallName.READ_DOCUMENT_METADATA;

    if (isListDataKeys) {
      if (Array.isArray(parsedContent)) {
        return (
          <div className="flex flex-wrap gap-2">
            {parsedContent.map((item: string) => (
              <Chip
                key={item}
                label={keyToLabel(item)}
                variant="outlined"
                size="small"
                color="default"
                sx={{ width: "fit-content" }}
              />
            ))}
          </div>
        );
      }

      const contentStr = String(parsedContent);
      return <MarkdownText>{contentStr}</MarkdownText>;
    }

    if (
      isReadDocumentMetadataTool &&
      typeof (parsedContent ?? toolResult.content) === "string"
    ) {
      const content = String(parsedContent ?? toolResult.content);
      return (
        <pre className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
          {content}
        </pre>
      );
    }

    if (isJsonContent) {
      return (
        <div className="text-sm">
          <JSONTree
            data={parsedContent}
            invertTheme={true}
            theme={JSON_TREE_THEME}
          />
        </div>
      );
    }

    let contentStr = String(parsedContent ?? toolResult.content);
    contentStr = contentStr.replace(/<br>/g, "\n");
    return <MarkdownText>{contentStr}</MarkdownText>;
  }, [
    inputPreview,
    isRejected,
    parsedToolResult,
    shouldShowInputPreview,
    toolConfig.showResult,
    toolName,
    toolResult,
  ]);

  if (
    toolName === ToolCallName.READ_DOCUMENT &&
    !approvalControls &&
    !isRejected
  ) {
    return (
      <ReadDocumentToolResult
        args={args}
        toolResult={toolResult}
        isLoading={isLoading}
      />
    );
  }

  return (
    <ToolCallFrame
      title={pendingTitle}
      icon={integrationIcon}
      isLoading={isLoading && !hasToolResult}
      hasError={hasError}
      isRejected={isRejected}
      isExpandable={hasExpandableContent || isPendingReview}
      expandedByDefault={isPendingReview || toolConfig.expandedByDefault}
      hasToolResult={hasToolResult}
      hasContent={shouldShowInputPreview || isPendingReview}
      footer={approvalControls}
    >
      {isPendingReview ? (
        <PendingDocumentToolParameters args={args} />
      ) : (
        toolResultContent
      )}
    </ToolCallFrame>
  );
};

GenericToolCall.displayName = "GenericToolCall";
